import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Check, AlertTriangle, Wand2, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { CFOPS, CSOSNS, CSTS_ICMS, ORIGENS, camposFaltando } from "../fiscalOptions";

/**
 * Um bloco por categoria na edição em massa do Módulo Fiscal.
 *
 * Fluxo pensado pro caso real: a loja tem 200 produtos e a classificação
 * fiscal costuma ser igual dentro da categoria (todo refrigerante tem o mesmo
 * NCM). Então preenche uma vez e aplica aos itens selecionados — por padrão,
 * só os que ainda estão pendentes, pra não sobrescrever ajuste manual.
 */
export default function CategoriaFiscalCard({ categoria, produtos, regime, onAplicar, salvando }) {
  const [aberto, setAberto] = useState(false);
  const [patch, setPatch] = useState({});
  const [selecionados, setSelecionados] = useState(null);

  const pendentes = useMemo(
    () => produtos.filter((p) => camposFaltando(p.fiscalConfig, regime).length > 0),
    [produtos, regime],
  );

  // null = ainda não mexeu, então vale o padrão "só os pendentes".
  const idsSelecionados = selecionados ?? new Set(pendentes.map((p) => p.id));
  const totalSel = idsSelecionados.size;

  const toggle = (id) => {
    const novo = new Set(idsSelecionados);
    if (novo.has(id)) novo.delete(id);
    else novo.add(id);
    setSelecionados(novo);
  };

  const toggleTodos = () => {
    setSelecionados(totalSel === produtos.length ? new Set() : new Set(produtos.map((p) => p.id)));
  };

  const temAlgoPraAplicar = Object.values(patch).some((v) => v !== "" && v != null);

  const aplicar = async () => {
    await onAplicar([...idsSelecionados], patch);
    setPatch({});
    setSelecionados(null);
  };

  const prontos = produtos.length - pendentes.length;
  const pct = produtos.length ? Math.round((prontos / produtos.length) * 100) : 0;

  return (
    <div className="bg-card rounded-2xl border shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-muted/30 transition-colors text-left"
      >
        {aberto ? (
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{categoria}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {produtos.length} {produtos.length === 1 ? "produto" : "produtos"} ·{" "}
            {pendentes.length === 0 ? (
              <span className="text-emerald-600 dark:text-emerald-400 font-medium">tudo pronto</span>
            ) : (
              <span className="text-amber-600 dark:text-amber-400 font-medium">
                {pendentes.length} pendente{pendentes.length > 1 ? "s" : ""}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2.5 shrink-0">
          <div className="w-20 h-1.5 rounded-full bg-muted overflow-hidden hidden sm:block">
            <div
              className={`h-full rounded-full ${pendentes.length === 0 ? "bg-emerald-500" : "bg-amber-500"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-[11px] font-mono text-muted-foreground tabular-nums">{pct}%</span>
        </div>
      </button>

      {aberto && (
        <div className="border-t">
          {/* Formulário aplicado em lote */}
          <div className="p-4 bg-muted/20 border-b space-y-3">
            <div className="flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-primary" />
              <Label className="text-xs font-semibold">Preencher de uma vez</Label>
              <span className="text-[10.5px] text-muted-foreground">
                campo em branco não altera nada
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[11px]">NCM</Label>
                <Input
                  value={patch.ncm || ""}
                  onChange={(e) =>
                    setPatch((p) => ({ ...p, ncm: e.target.value.replace(/\D/g, "").slice(0, 8) }))
                  }
                  placeholder="00000000"
                  inputMode="numeric"
                  className="h-9 text-sm font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px]">CFOP</Label>
                <Select value={patch.cfop || ""} onValueChange={(v) => setPatch((p) => ({ ...p, cfop: v }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Manter" /></SelectTrigger>
                  <SelectContent>
                    {CFOPS.map((o) => (
                      <SelectItem key={o.v} value={o.v} className="text-sm">{o.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px]">Origem</Label>
                <Select value={patch.origem || ""} onValueChange={(v) => setPatch((p) => ({ ...p, origem: v }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Manter" /></SelectTrigger>
                  <SelectContent>
                    {ORIGENS.map((o) => (
                      <SelectItem key={o.v} value={o.v} className="text-sm">{o.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px]">{regime === "simples" ? "CSOSN" : "CST do ICMS"}</Label>
                <Select
                  value={(regime === "simples" ? patch.csosn : patch.cst_icms) || ""}
                  onValueChange={(v) =>
                    setPatch((p) => ({ ...p, [regime === "simples" ? "csosn" : "cst_icms"]: v }))
                  }
                >
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Manter" /></SelectTrigger>
                  <SelectContent>
                    {(regime === "simples" ? CSOSNS : CSTS_ICMS).map((o) => (
                      <SelectItem key={o.v} value={o.v} className="text-sm">{o.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 pt-1">
              <button
                type="button"
                onClick={toggleTodos}
                className="text-[11px] text-primary hover:underline font-medium"
              >
                {totalSel === produtos.length ? "Desmarcar todos" : "Selecionar todos"}
              </button>
              <Button
                type="button"
                size="sm"
                disabled={!temAlgoPraAplicar || totalSel === 0 || salvando}
                onClick={aplicar}
                className="h-9"
              >
                {salvando ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aplicando...</>
                ) : (
                  <>Aplicar a {totalSel} {totalSel === 1 ? "produto" : "produtos"}</>
                )}
              </Button>
            </div>
          </div>

          {/* Lista de produtos da categoria */}
          <div className="divide-y max-h-80 overflow-y-auto">
            {produtos.map((p) => {
              const faltando = camposFaltando(p.fiscalConfig, regime);
              const ok = faltando.length === 0;
              return (
                <label
                  key={p.id}
                  className="flex items-center gap-3 px-5 py-2.5 hover:bg-muted/20 cursor-pointer"
                >
                  <Checkbox
                    checked={idsSelecionados.has(p.id)}
                    onCheckedChange={() => toggle(p.id)}
                  />
                  <span className="flex-1 min-w-0 text-sm truncate">{p.name}</span>
                  {ok ? (
                    <span className="flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400 shrink-0">
                      <Check className="w-3.5 h-3.5" /> Pronto
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400 shrink-0">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">falta {faltando.join(", ")}</span>
                      <span className="sm:hidden">{faltando.length}</span>
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
